import { useCallback, useEffect, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import { getLessonComments } from "../../services/comment";
import CommentCard from "./CommentCard";

const COMMENTS_LIMIT = 10;
const LOAD_MORE_THRESHOLD = 0.85;

export const Comments = ({ lessonId }: { lessonId?: number | null }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    direction: "rtl",
    align: "start",
    containScroll: "trimSnaps",
    slidesToScroll: 1,
    breakpoints: {
      "(min-width: 768px)": { slidesToScroll: 2 },
      "(min-width: 1280px)": { slidesToScroll: 3 },
    },
  });

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["lesson-comments", lessonId],
    queryFn: ({ pageParam }) =>
      getLessonComments({
        lessonId: lessonId || 11,
        limit: COMMENTS_LIMIT,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < COMMENTS_LIMIT ? undefined : allPages.length + 1,
    enabled: !!lessonId,
  });

  const comments = data?.pages.flat() ?? [];
  const loadingRef = useRef(false);

  const onScroll = useCallback(() => {
    if (!emblaApi) return;
    const { scrollProgress } = emblaApi;
    if (loadingRef.current) return;
    if (!hasNextPage) return;
    if (scrollProgress() >= LOAD_MORE_THRESHOLD) {
      fetchNextPage();
    }
  }, [emblaApi, hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("scroll", onScroll);
    emblaApi.on("settle", onScroll);
    return () => {
      emblaApi.off("scroll", onScroll);
      emblaApi.off("settle", onScroll);
    };
  }, [emblaApi, onScroll]);

  useEffect(() => {
    loadingRef.current = isFetchingNextPage;
  }, [isFetchingNextPage]);

  const SkeletonCard = () => (
    <div className="w-[371px] h-[166px] px-4 m-auto relative animate-pulse">
      <div className="absolute -right-4 top-0 bg-gray-400 rounded-full w-[62px] h-[62px]" />
      <div className="flex flex-col justify-between h-full border-r-2 border-white pr-10">
        <div className="flex items-center justify-between mt-2">
          <div className="h-6 bg-gray-400 rounded w-24" />
          <div className="flex gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="w-5 h-5 bg-gray-400 rounded-sm" />
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-400 rounded w-3/4" />
          <div className="h-4 bg-gray-400 rounded w-3/4" />
          <div className="h-4 bg-gray-400 rounded w-3/4" />
          <div className="h-4 bg-gray-400 rounded w-3/4" />
          <div className="h-4 bg-gray-400 rounded w-1/2" />
        </div>
      </div>
    </div>
  );

  return (
    <section className="container h-full relative my-20">
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full gap-4 items-center">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="flex-[0_0_100%] md:flex-[0_0_calc(50%_-_0.5rem)] xl:flex-[0_0_calc(33.3333%_-_0.6667rem)] pr-0 flex items-center justify-center"
              >
                <SkeletonCard />
              </div>
            ))
          ) : comments.length ? (
            <>
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="flex-[0_0_100%] md:flex-[0_0_calc(50%_-_0.5rem)] xl:flex-[0_0_calc(33.3333%_-_0.6667rem)] h-[200px] flex items-center justify-center"
                >
                  <CommentCard comment={comment} />
                </div>
              ))}
              {isFetchingNextPage && (
                <div className="flex-[0_0_100%] md:flex-[0_0_calc(50%_-_0.5rem)] xl:flex-[0_0_calc(33.3333%_-_0.6667rem)] h-[200px] flex items-center justify-center">
                  <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-white font-kalameh text-lg w-full mt-8">
              نظری ثبت نشده است.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
